'use client';

import { useMemo } from 'react';

import type { PartiallyRequiredType } from '../../../types/partially-required';
import { CalendarControlButton } from '../calendar-control-button';
import { CalendarDay } from '../calendar-day';
import { CalendarDayOfWeek } from '../calendar-day-of-week';
import { CalendarMonth } from '../calendar-month';
import { CalendarYearSelect } from '../calendar-year-select';
import type { CalendarBodyPropsInterface } from '../types/calendar-body.types';
import type { CalendarControlsPropsInterface } from '../types/calendar-controls.types';

export const useCalendarBodyRenderProps = (props: CalendarBodyPropsInterface) => {
  const { renderDay, renderDayOfWeek, ...rest } = props;

  const defaultRenderDay: typeof renderDay = (renderProps) => {
    const { day, ...renderPropsRest } = renderProps;
    return <CalendarDay {...renderPropsRest}>{day}</CalendarDay>;
  };

  const defaultRenderDayOfWeek: typeof renderDayOfWeek = (renderProps) => {
    const { day, ...renderPropsRest } = renderProps;
    return <CalendarDayOfWeek {...renderPropsRest}>{day.charAt(0)}</CalendarDayOfWeek>;
  };

  return useMemo<
    PartiallyRequiredType<CalendarBodyPropsInterface, 'renderDay' | 'renderDayOfWeek'>
  >(
    () => ({
      renderDay: renderDay ?? defaultRenderDay,
      renderDayOfWeek: renderDayOfWeek ?? defaultRenderDayOfWeek,
      ...rest,
    }),
    // No need to observe rest since it won't affect any render props
    // eslint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps)
    [renderDay, renderDayOfWeek],
  );
};

export const useCalendarControlsRenderProps = (props: CalendarControlsPropsInterface) => {
  const { renderYearSelect, renderMonth, renderNextMonth, renderPrevMonth, ...rest } = props;

  const defaultRenderYearSelect: typeof renderYearSelect = (renderProps) => (
    <CalendarYearSelect {...renderProps} />
  );

  const defaultRenderMonth: typeof renderMonth = (renderProps) => {
    const { children, ...renderPropsRest } = renderProps;
    return <CalendarMonth {...renderPropsRest}>{children}</CalendarMonth>;
  };

  const defaultRenderButton: typeof renderNextMonth = (renderProps) => (
    <CalendarControlButton {...renderProps} />
  );

  return useMemo<
    PartiallyRequiredType<
      CalendarControlsPropsInterface,
      'renderYearSelect' | 'renderMonth' | 'renderNextMonth' | 'renderPrevMonth'
    >
  >(
    () => ({
      renderYearSelect: renderYearSelect ?? defaultRenderYearSelect,
      renderMonth: renderMonth ?? defaultRenderMonth,
      renderNextMonth: renderNextMonth ?? defaultRenderButton,
      renderPrevMonth: renderPrevMonth ?? defaultRenderButton,
      ...rest,
    }),
    // No need to observe rest since it won't affect any render props
    // eslint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps)
    [renderYearSelect, renderMonth, renderNextMonth, renderPrevMonth],
  );
};
